import {
  Button as FoundationButton,
  buttonVariants as foundationButtonVariants,
  type ButtonProps as FoundationButtonProps,
  type ButtonSize,
  type ButtonVariant,
} from "@storyos/design-system/components/ui/button"

import { cn } from "~/lib/utils"

type StoryOsButtonSize = ButtonSize | "lg" | "icon-xs" | "icon-sm" | "icon-lg"

type ButtonVariantOptions = {
  className?: string
  size?: StoryOsButtonSize | null
  variant?: ButtonVariant | null
}

type ButtonProps = Omit<FoundationButtonProps, "size"> & {
  size?: StoryOsButtonSize
}

const compatibilitySizeClassNames: Partial<Record<StoryOsButtonSize, string>> =
  {
    lg: "h-10 min-h-10 min-w-10 px-4 py-2.5",
    "icon-xs": "size-6 min-h-6 min-w-6 rounded-md [&_svg]:size-3",
    "icon-sm": "size-8 min-h-8 min-w-8 rounded-md",
    "icon-lg": "size-10 min-h-10 min-w-10",
  }

function toFoundationButtonSize(size: StoryOsButtonSize | null | undefined) {
  switch (size) {
    case "lg":
      return "default"
    case "icon-xs":
    case "icon-sm":
    case "icon-lg":
      return "icon"
    default:
      return size ?? "default"
  }
}

function buttonVariants({
  className,
  size,
  variant,
}: ButtonVariantOptions = {}) {
  const normalizedSize = size ?? "default"

  return foundationButtonVariants({
    variant,
    size: toFoundationButtonSize(normalizedSize),
    className: cn(compatibilitySizeClassNames[normalizedSize], className),
  })
}

function Button({
  children,
  className,
  size = "default",
  ...props
}: ButtonProps) {
  const normalizedSize = size ?? "default"

  return (
    <FoundationButton
      data-size={normalizedSize}
      size={toFoundationButtonSize(normalizedSize)}
      className={cn(compatibilitySizeClassNames[normalizedSize], className)}
      {...props}
    >
      {children}
    </FoundationButton>
  )
}

export { Button, buttonVariants, type ButtonProps }
